import { lazy, Suspense, type ComponentType, type ReactNode } from 'react';
import { createBrowserRouter, Outlet, useRouteError } from 'react-router-dom';
import { AppShell } from './components/AppShell';
import { ButtonLink } from './components/Button';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Spinner } from './components/Spinner';
import styles from './routes/screens.module.css';
import { lazyChunk } from './lib/lazyChunk';

/* ---------------------------------------------------------------- *
 * Marketing lives in src/marketing and is code-split away from the
 * studio: a visitor reading the manifesto never downloads the app.
 * ---------------------------------------------------------------- */

const marketing = lazyChunk('marketing', () => import('./marketing'));

const MarketingLayout = lazy(() => marketing().then((m) => ({ default: m.MarketingLayout })));
const LandingPage = lazy(() => marketing().then((m) => ({ default: m.LandingPage })));
const FeaturesPage = lazy(() => marketing().then((m) => ({ default: m.FeaturesPage })));
const CreatorsPage = lazy(() => marketing().then((m) => ({ default: m.CreatorsPage })));
const PricingPage = lazy(() => marketing().then((m) => ({ default: m.PricingPage })));
const ManifestoPage = lazy(() => marketing().then((m) => ({ default: m.ManifestoPage })));
const PrivacyPage = lazy(() => marketing().then((m) => ({ default: m.PrivacyPage })));
const TermsPage = lazy(() => marketing().then((m) => ({ default: m.TermsPage })));
const OutsideLandsPage = lazy(() => marketing().then((m) => ({ default: m.OutsideLandsPage })));

const AuthPage = lazy(lazyChunk('AuthPage', () => import('./routes/AuthPage')));
const ForgotPassword = lazy(lazyChunk('ForgotPassword', () => import('./routes/ForgotPassword')));
const ResetPassword = lazy(lazyChunk('ResetPassword', () => import('./routes/ResetPassword')));
const Onboarding = lazy(lazyChunk('Onboarding', () => import('./routes/Onboarding')));
const Rack = lazy(lazyChunk('Rack', () => import('./routes/Rack')));
const Studio = lazy(lazyChunk('Studio', () => import('./routes/Studio')));
const Looks = lazy(lazyChunk('Looks', () => import('./routes/Looks')));
const LookDetail = lazy(lazyChunk('LookDetail', () => import('./routes/LookDetail')));
const Rooms = lazy(lazyChunk('Rooms', () => import('./routes/Rooms')));
const Room = lazy(lazyChunk('Room', () => import('./routes/Room')));
const Runway = lazy(lazyChunk('Runway', () => import('./routes/Runway')));
const Savant = lazy(lazyChunk('Savant', () => import('./routes/Savant')));
const Me = lazy(lazyChunk('Me', () => import('./routes/Me')));
// Operator-only and deliberately absent from every nav. Its own chunk, so a
// normal user never downloads the annotation studio.
const Eval = lazy(lazyChunk('Eval', () => import('./routes/Eval')));
const PublicProfile = lazy(lazyChunk('PublicProfile', () => import('./routes/PublicProfile')));
const NotFound = lazy(lazyChunk('NotFound', () => import('./routes/NotFound')));

function Loading() {
  return (
    <div style={{ minHeight: '60vh', display: 'grid', placeItems: 'center' }}>
      <Spinner size={24} label="Loading" />
    </div>
  );
}

function view(Component: ComponentType): ReactNode {
  return (
    <Suspense fallback={<Loading />}>
      <Component />
    </Suspense>
  );
}

function StudioLayout() {
  return (
    <ProtectedRoute>
      <AppShell>
        <Outlet />
      </AppShell>
    </ProtectedRoute>
  );
}

function RouteError() {
  const error = useRouteError();
  if (import.meta.env.DEV) console.error('[racksavant] route error', error);
  return (
    <div className={styles.notFound}>
      <div className={styles.notFoundInner}>
        <p className={styles.eyebrow}>Something slipped</p>
        <h1 className={styles.notFoundTitle}>Off the hanger</h1>
        <p className={styles.pageSub}>
          That page fell over on the way in. Nothing you made is lost.
        </p>
        <div className={styles.headActions} style={{ justifyContent: 'center', marginLeft: 0 }}>
          <ButtonLink to="/app" variant="primary">
            Back to the rack
          </ButtonLink>
        </div>
      </div>
    </div>
  );
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: view(MarketingLayout),
    errorElement: <RouteError />,
    children: [
      { index: true, element: view(LandingPage) },
      { path: 'features', element: view(FeaturesPage) },
      { path: 'creators', element: view(CreatorsPage) },
      { path: 'pricing', element: view(PricingPage) },
      { path: 'manifesto', element: view(ManifestoPage) },
      { path: 'privacy', element: view(PrivacyPage) },
      { path: 'terms', element: view(TermsPage) },
      // Hackathon entry for the Outside Lands festival. Linked from the footer
      // rather than the primary nav — it is a campaign page, not a fifth pillar.
      { path: 'outsidelands', element: view(OutsideLandsPage) },
    ],
  },
  { path: '/auth', element: view(AuthPage), errorElement: <RouteError /> },
  // Both are signed-out routes by design: forgetting a password is the one
  // moment a user provably cannot get past /auth.
  { path: '/auth/forgot', element: view(ForgotPassword), errorElement: <RouteError /> },
  { path: '/auth/reset', element: view(ResetPassword), errorElement: <RouteError /> },
  {
    path: '/app/onboarding',
    element: <ProtectedRoute>{view(Onboarding)}</ProtectedRoute>,
    errorElement: <RouteError />,
  },
  {
    path: '/app',
    element: <StudioLayout />,
    errorElement: <RouteError />,
    children: [
      { index: true, element: view(Rack) },
      { path: 'studio', element: view(Studio) },
      { path: 'looks', element: view(Looks) },
      { path: 'looks/:id', element: view(LookDetail) },
      { path: 'rooms', element: view(Rooms) },
      { path: 'rooms/:code', element: view(Room) },
      { path: 'runway', element: view(Runway) },
      { path: 'savant', element: view(Savant) },
      { path: 'me', element: view(Me) },
      // Unlisted: no link points here. The route itself renders a 404 for
      // anyone the API does not confirm as an operator.
      { path: 'eval', element: view(Eval) },
    ],
  },
  { path: '/u/:username', element: view(PublicProfile), errorElement: <RouteError /> },
  { path: '*', element: view(NotFound) },
]);
